import React, { useState, useEffect } from 'react';
import {
  Box,
  Container,
  Grid,
  Paper,
  Typography,
  Button,
  Card,
  CardContent,
  Tab,
  Tabs,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  Chip,
  IconButton,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  Alert,
  Divider,
  List,
  ListItem,
  ListItemText,
  ListItemSecondaryAction,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  CircularProgress,
  Tooltip,
  FormControlLabel,
  Switch,
  Checkbox,
  FormGroup,
  LinearProgress,
  Avatar,
  AvatarGroup
} from '@mui/material';
import {
  Email as EmailIcon,
  Send as SendIcon,
  Add as AddIcon,
  Edit as EditIcon,
  Delete as DeleteIcon,
  ContentCopy as CopyIcon,
  Schedule as ScheduleIcon,
  PersonAdd as PersonAddIcon,
  Analytics as AnalyticsIcon,
  Code as TemplateIcon,
  Preview as PreviewIcon,
  Science as TestIcon,
  Group as GroupIcon,
  TrendingUp as TrendingUpIcon,
  OpenInNew as OpenIcon,
  TouchApp as ClickIcon,
  Unsubscribe as UnsubscribeIcon,
  Timer as TimerIcon,
  Visibility as VisibilityIcon
} from '@mui/icons-material';
import { DateTimePicker } from '@mui/x-date-pickers/DateTimePicker';
import dayjs from 'dayjs';
import { useAuth } from '../contexts/AuthContext';
import api from '../services/api';

interface TabPanelProps {
  children?: React.ReactNode;
  index: number;
  value: number;
}

function TabPanel(props: TabPanelProps) {
  const { children, value, index, ...other } = props;

  return (
    <div
      role="tabpanel"
      hidden={value !== index}
      id={`email-tabpanel-${index}`}
      aria-labelledby={`email-tab-${index}`}
      {...other}
    >
      {value === index && <Box sx={{ p: 3 }}>{children}</Box>}
    </div>
  );
}

const EmailPage: React.FC = () => {
  const { user } = useAuth();
  const [tabValue, setTabValue] = useState(0);
  const [campaigns, setCampaigns] = useState<any[]>([]);
  const [templates, setTemplates] = useState<any[]>([]);
  const [loading, setLoading] = useState(true);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCampaigns, setTotalCampaigns] = useState(0);
  const [analytics, setAnalytics] = useState<any>(null);
  
  // Campaign form state
  const [campaignDialog, setCampaignDialog] = useState(false);
  const [selectedCampaign, setSelectedCampaign] = useState<any>(null);
  const [campaignForm, setCampaignForm] = useState({
    name: '',
    subject: '',
    preheader: '',
    type: 'Newsletter',
    content: {
      html: '',
      text: ''
    },
    scheduling: {
      sendAt: null as any,
      timezone: 'America/New_York',
      sendingSpeed: 'Immediate'
    },
    audience: {
      filters: {
        memberTier: [] as string[],
        tags: [] as string[],
        isLoyaltyMember: false
      }
    },
    personalization: {
      useFirstName: true,
      fallbackName: 'Valued Guest',
      loyaltyStatus: false,
      upcomingReservations: false
    }
  });

  // Template dialog state
  const [templateDialog, setTemplateDialog] = useState(false);
  const [previewDialog, setPreviewDialog] = useState(false);
  const [testDialog, setTestDialog] = useState(false);
  const [testEmail, setTestEmail] = useState('');
  
  // Filter state
  const [statusFilter, setStatusFilter] = useState('');
  const [typeFilter, setTypeFilter] = useState('');

  useEffect(() => {
    fetchCampaigns();
    fetchTemplates();
    fetchAnalytics();
  }, [page, rowsPerPage, statusFilter, typeFilter]);

  const fetchCampaigns = async () => {
    try {
      setLoading(true);
      const params = new URLSearchParams({
        page: (page + 1).toString(),
        limit: rowsPerPage.toString()
      });
      
      if (statusFilter) params.append('status', statusFilter);
      if (typeFilter) params.append('type', typeFilter);
      
      const response = await api.get(
        `/email/campaigns?${params}`
      );
      
      setCampaigns(response.data?.campaigns || []);
      setTotalCampaigns(response.data?.totalCampaigns || 0);
    } catch (error) {
      console.error('Error fetching campaigns:', error);
    } finally {
      setLoading(false);
    }
  };

  const fetchTemplates = async () => {
    try {
      const response = await api.get(
        `/email/templates`
      );
      setTemplates(response.data || []);
    } catch (error) {
      console.error('Error fetching templates:', error);
    }
  };

  const fetchAnalytics = async () => {
    try {
      const endDate = new Date();
      const startDate = new Date();
      startDate.setDate(startDate.getDate() - 30);
      
      const response = await api.get(
        `/email/analytics`,
        {
          params: {
            startDate: startDate.toISOString(),
            endDate: endDate.toISOString()
          }
        }
      );
      setAnalytics(response.data);
    } catch (error) {
      console.error('Error fetching analytics:', error);
    }
  };

  const handleCreateCampaign = () => {
    setSelectedCampaign(null);
    setCampaignForm({
      name: '',
      subject: '',
      preheader: '',
      type: 'Newsletter',
      content: {
        html: '',
        text: ''
      },
      scheduling: {
        sendAt: null,
        timezone: 'America/New_York',
        sendingSpeed: 'Immediate'
      },
      audience: {
        filters: {
          memberTier: [],
          tags: [],
          isLoyaltyMember: false
        }
      },
      personalization: {
        useFirstName: true,
        fallbackName: 'Valued Guest',
        loyaltyStatus: false,
        upcomingReservations: false
      }
    });
    setCampaignDialog(true);
  };

  const handleEditCampaign = (campaign: any) => {
    setSelectedCampaign(campaign);
    setCampaignForm({
      name: campaign.name,
      subject: campaign.subject,
      preheader: campaign.preheader || '',
      type: campaign.type || 'Newsletter',
      content: campaign.content || { html: '', text: '' },
      scheduling: campaign.scheduling || { sendAt: null, timezone: 'America/New_York', sendingSpeed: 'Immediate' },
      audience: campaign.audience || { filters: { memberTier: [], tags: [], isLoyaltyMember: false } },
      personalization: campaign.personalization || { useFirstName: true, fallbackName: 'Valued Guest', loyaltyStatus: false, upcomingReservations: false }
    });
    setCampaignDialog(true);
  };

  const handleSaveCampaign = async () => {
    try {
      if (selectedCampaign) {
        await api.put(
          `/email/campaigns/${selectedCampaign._id}`,
          campaignForm
        );
      } else {
        await api.post(
          `/email/campaigns`,
          campaignForm
        );
      }
      
      setCampaignDialog(false);
      fetchCampaigns();
    } catch (error) {
      console.error('Error saving campaign:', error);
    }
  };

  const handleSendCampaign = async (campaignId: string) => {
    if (window.confirm('Are you sure you want to send this campaign now?')) {
      try {
        await api.post(
          `/email/campaigns/${campaignId}/send`,
          {}
        );
        fetchCampaigns();
      } catch (error) {
        console.error('Error sending campaign:', error);
      }
    }
  };

  const handleTestCampaign = async () => {
    try {
      await api.post(
        `/email/campaigns/${selectedCampaign._id}/test`,
        { testEmail }
      );
      setTestDialog(false);
      alert('Test email sent successfully!');
    } catch (error) {
      console.error('Error sending test email:', error);
    }
  };

  const handleCloneCampaign = async (campaignId: string) => {
    const name = prompt('Enter name for cloned campaign:');
    if (name) {
      try {
        await api.post(
          `/email/campaigns/${campaignId}/clone`,
          { name }
        );
        fetchCampaigns();
      } catch (error) {
        console.error('Error cloning campaign:', error);
      }
    }
  };

  const handleDeleteCampaign = async (campaignId: string) => {
    if (window.confirm('Are you sure you want to delete this campaign?')) {
      try {
        await api.delete(
          `/email/campaigns/${campaignId}`
        );
        fetchCampaigns();
      } catch (error) {
        console.error('Error deleting campaign:', error);
      }
    }
  };

  const handleCreateFromTemplate = async (templateId: string) => {
    try {
      const campaignData = {
        name: `New Campaign - ${new Date().toLocaleDateString()}`,
        subject: 'Your Subject Here',
        type: 'Newsletter'
      };
      
      await api.post(
        `/email/templates/${templateId}/create-campaign`,
        campaignData
      );
      
      setTabValue(0);
      fetchCampaigns();
    } catch (error) {
      console.error('Error creating campaign from template:', error);
    }
  };

  const getStatusColor = (status: string) => {
    const colors: any = {
      'Draft': 'default',
      'Scheduled': 'info',
      'Sending': 'warning',
      'Sent': 'success',
      'Paused': 'warning',
      'Failed': 'error'
    };
    return colors[status] || 'default';
  };

  const getCampaignTypeIcon = (type: string) => {
    const icons: any = {
      'Newsletter': <EmailIcon />,
      'Promotional': <TrendingUpIcon />,
      'Welcome': <PersonAddIcon />,
      'Transactional': <SendIcon />
    };
    return icons[type] || <EmailIcon />;
  };

  return (
    <Container maxWidth="lg">
      <Box sx={{ mb: 4 }}>
        <Typography variant="h4" gutterBottom>
          Email Marketing
        </Typography>
        <Typography variant="body1" color="text.secondary">
          Create and manage email campaigns, templates, and automation
        </Typography>
      </Box>

      {/* Analytics Summary */}
      {analytics && (
        <Grid container spacing={3} sx={{ mb: 4 }}>
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <EmailIcon color="primary" sx={{ mr: 2 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Total Sent
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {analytics.overall?.totalSent?.toLocaleString() || 0}
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Last 30 days
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <OpenIcon color="success" sx={{ mr: 2 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Avg Open Rate
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {analytics.overall?.avgOpenRate?.toFixed(1) || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Industry avg: 22%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <ClickIcon color="info" sx={{ mr: 2 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Avg Click Rate
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {analytics.overall?.avgClickRate?.toFixed(1) || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Industry avg: 2.5%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
          
          <Grid item xs={12} sm={6} md={3}>
            <Card>
              <CardContent>
                <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                  <UnsubscribeIcon color="error" sx={{ mr: 2 }} />
                  <Typography color="text.secondary" variant="subtitle2">
                    Unsubscribe Rate
                  </Typography>
                </Box>
                <Typography variant="h4">
                  {analytics.overall?.avgUnsubscribeRate?.toFixed(2) || 0}%
                </Typography>
                <Typography variant="body2" color="text.secondary">
                  Industry avg: 0.1%
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>
      )}

      <Paper>
        <Tabs value={tabValue} onChange={(e, v) => setTabValue(v)}>
          <Tab label="Campaigns" icon={<EmailIcon />} iconPosition="start" />
          <Tab label="Templates" icon={<TemplateIcon />} iconPosition="start" />
          <Tab label="Automation" icon={<TimerIcon />} iconPosition="start" />
          <Tab label="Analytics" icon={<AnalyticsIcon />} iconPosition="start" />
        </Tabs>

        <TabPanel value={tabValue} index={0}>
          {/* Campaigns Tab */}
          <Box sx={{ mb: 3, display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
            <Box sx={{ display: 'flex', gap: 2 }}>
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Status</InputLabel>
                <Select
                  value={statusFilter}
                  label="Status"
                  onChange={(e) => setStatusFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Draft">Draft</MenuItem>
                  <MenuItem value="Scheduled">Scheduled</MenuItem>
                  <MenuItem value="Sent">Sent</MenuItem>
                </Select>
              </FormControl>
              
              <FormControl size="small" sx={{ minWidth: 150 }}>
                <InputLabel>Type</InputLabel>
                <Select
                  value={typeFilter}
                  label="Type"
                  onChange={(e) => setTypeFilter(e.target.value)}
                >
                  <MenuItem value="">All</MenuItem>
                  <MenuItem value="Newsletter">Newsletter</MenuItem>
                  <MenuItem value="Promotional">Promotional</MenuItem>
                  <MenuItem value="Welcome">Welcome</MenuItem>
                  <MenuItem value="Transactional">Transactional</MenuItem>
                </Select>
              </FormControl>
            </Box>
            
            <Button
              variant="contained"
              startIcon={<AddIcon />}
              onClick={handleCreateCampaign}
            >
              Create Campaign
            </Button>
          </Box>

          <TableContainer>
            <Table>
              <TableHead>
                <TableRow>
                  <TableCell>Campaign</TableCell>
                  <TableCell>Type</TableCell>
                  <TableCell>Status</TableCell>
                  <TableCell>Recipients</TableCell>
                  <TableCell>Open Rate</TableCell>
                  <TableCell>Click Rate</TableCell>
                  <TableCell>Created</TableCell>
                  <TableCell>Actions</TableCell>
                </TableRow>
              </TableHead>
              <TableBody>
                {loading ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      <CircularProgress />
                    </TableCell>
                  </TableRow>
                ) : campaigns.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} align="center">
                      No campaigns found
                    </TableCell>
                  </TableRow>
                ) : (
                  campaigns.map((campaign) => (
                    <TableRow key={campaign._id}>
                      <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center' }}>
                          {getCampaignTypeIcon(campaign.type)}
                          <Box sx={{ ml: 2 }}>
                            <Typography variant="subtitle2">
                              {campaign.name}
                            </Typography>
                            <Typography variant="body2" color="text.secondary">
                              {campaign.subject}
                            </Typography>
                          </Box>
                        </Box>
                      </TableCell>
                      <TableCell>{campaign.type}</TableCell>
                      <TableCell>
                        <Chip
                          label={campaign.status}
                          color={getStatusColor(campaign.status)}
                          size="small"
                        />
                      </TableCell>
                      <TableCell>{campaign.recipients?.total || 0}</TableCell>
                      <TableCell>
                        {campaign.metrics?.openRate || 0}%
                      </TableCell>
                      <TableCell>
                        {campaign.metrics?.clickRate || 0}%
                      </TableCell>
                      <TableCell>
                        {dayjs(campaign.createdAt).format('MMM D, YYYY')}
                      </TableCell>
                      <TableCell>
                        <Stack direction="row" spacing={1}>
                          {campaign.status === 'Draft' && (
                            <>
                              <Tooltip title="Edit">
                                <IconButton
                                  size="small"
                                  onClick={() => handleEditCampaign(campaign)}
                                >
                                  <EditIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                              <Tooltip title="Send Test">
                                <IconButton
                                  size="small"
                                  onClick={() => {
                                    setSelectedCampaign(campaign);
                                    setTestDialog(true);
                                  }}
                                >
                                  <TestIcon fontSize="small" />
                                </IconButton>
                              </Tooltip>
                            </>
                          )}
                          
                          {campaign.status === 'Scheduled' && (
                            <Tooltip title="Send Now">
                              <IconButton
                                size="small"
                                onClick={() => handleSendCampaign(campaign._id)}
                              >
                                <SendIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          <Tooltip title="Clone">
                            <IconButton
                              size="small"
                              onClick={() => handleCloneCampaign(campaign._id)}
                            >
                              <CopyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                          
                          {campaign.status === 'Sent' && (
                            <Tooltip title="View Analytics">
                              <IconButton
                                size="small"
                                onClick={() => {
                                  setSelectedCampaign(campaign);
                                  setTabValue(3);
                                }}
                              >
                                <AnalyticsIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                          
                          {(campaign.status === 'Draft' || campaign.status === 'Cancelled') && (
                            <Tooltip title="Delete">
                              <IconButton
                                size="small"
                                onClick={() => handleDeleteCampaign(campaign._id)}
                              >
                                <DeleteIcon fontSize="small" />
                              </IconButton>
                            </Tooltip>
                          )}
                        </Stack>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </TableContainer>
          
          <TablePagination
            component="div"
            count={totalCampaigns}
            page={page}
            onPageChange={(e, newPage) => setPage(newPage)}
            rowsPerPage={rowsPerPage}
            onRowsPerPageChange={(e) => {
              setRowsPerPage(parseInt(e.target.value, 10));
              setPage(0);
            }}
          />
        </TabPanel>

        <TabPanel value={tabValue} index={1}>
          {/* Templates Tab */}
          <Grid container spacing={3}>
            {templates.map((template) => (
              <Grid item xs={12} sm={6} md={4} key={template._id}>
                <Card>
                  <CardContent>
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', mb: 2 }}>
                      <Typography variant="h6">
                        {template.name}
                      </Typography>
                      <Chip
                        label={template.type}
                        size="small"
                        color="primary"
                      />
                    </Box>
                    
                    <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                      {template.description || 'No description'}
                    </Typography>
                    
                    <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 2 }}>
                      <Typography variant="caption" color="text.secondary">
                        Used {template.usage?.timesUsed || 0} times
                      </Typography>
                      {template.usage?.performance?.avgOpenRate > 0 && (
                        <Typography variant="caption" color="success.main">
                          {template.usage.performance.avgOpenRate.toFixed(1)}% open rate
                        </Typography>
                      )}
                    </Box>
                    
                    <Stack direction="row" spacing={1}>
                      <Button
                        size="small"
                        startIcon={<PreviewIcon />}
                        onClick={() => {
                          // Preview template
                        }}
                      >
                        Preview
                      </Button>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<AddIcon />}
                        onClick={() => handleCreateFromTemplate(template._id)}
                      >
                        Use Template
                      </Button>
                    </Stack>
                  </CardContent>
                </Card>
              </Grid>
            ))}
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={2}>
          {/* Automation Tab */}
          <Alert severity="info" sx={{ mb: 3 }}>
            Email automation runs automatically based on triggers you configure
          </Alert>
          
          <Grid container spacing={3}>
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Welcome Series
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Automatically send welcome emails to new guests
                  </Typography>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enabled"
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Check-in Reminders
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Send reminders 24 hours before check-in
                  </Typography>
                  <FormControlLabel
                    control={<Switch defaultChecked />}
                    label="Enabled"
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Abandoned Cart Recovery
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Remind guests about incomplete bookings
                  </Typography>
                  <FormControlLabel
                    control={<Switch />}
                    label="Enabled"
                  />
                </CardContent>
              </Card>
            </Grid>
            
            <Grid item xs={12} md={6}>
              <Card>
                <CardContent>
                  <Typography variant="h6" gutterBottom>
                    Re-engagement Campaign
                  </Typography>
                  <Typography variant="body2" color="text.secondary" paragraph>
                    Win back inactive guests after 6 months
                  </Typography>
                  <FormControlLabel
                    control={<Switch />}
                    label="Enabled"
                  />
                </CardContent>
              </Card>
            </Grid>
          </Grid>
        </TabPanel>

        <TabPanel value={tabValue} index={3}>
          {/* Analytics Tab */}
          {analytics && (
            <Grid container spacing={3}>
              <Grid item xs={12}>
                <Typography variant="h6" gutterBottom>
                  Campaign Performance by Type
                </Typography>
                <TableContainer component={Paper} variant="outlined">
                  <Table>
                    <TableHead>
                      <TableRow>
                        <TableCell>Type</TableCell>
                        <TableCell align="right">Campaigns</TableCell>
                        <TableCell align="right">Total Sent</TableCell>
                        <TableCell align="right">Avg Open Rate</TableCell>
                        <TableCell align="right">Avg Click Rate</TableCell>
                        <TableCell align="right">Revenue</TableCell>
                      </TableRow>
                    </TableHead>
                    <TableBody>
                      {analytics.byType?.map((type: any) => (
                        <TableRow key={type._id}>
                          <TableCell>{type._id}</TableCell>
                          <TableCell align="right">{type.campaigns}</TableCell>
                          <TableCell align="right">{type.totalSent.toLocaleString()}</TableCell>
                          <TableCell align="right">{type.avgOpenRate.toFixed(1)}%</TableCell>
                          <TableCell align="right">{type.avgClickRate.toFixed(1)}%</TableCell>
                          <TableCell align="right">${type.totalRevenue?.toFixed(2) || '0.00'}</TableCell>
                        </TableRow>
                      ))}
                    </TableBody>
                  </Table>
                </TableContainer>
              </Grid>
            </Grid>
          )}
        </TabPanel>
      </Paper>

      {/* Campaign Dialog */}
      <Dialog
        open={campaignDialog}
        onClose={() => setCampaignDialog(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedCampaign ? 'Edit Campaign' : 'Create Campaign'}
        </DialogTitle>
        <DialogContent dividers>
          <Grid container spacing={3}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Campaign Name"
                value={campaignForm.name}
                onChange={(e) => setCampaignForm({ ...campaignForm, name: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Subject Line"
                value={campaignForm.subject}
                onChange={(e) => setCampaignForm({ ...campaignForm, subject: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <TextField
                fullWidth
                label="Preheader Text"
                value={campaignForm.preheader}
                onChange={(e) => setCampaignForm({ ...campaignForm, preheader: e.target.value })}
              />
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <FormControl fullWidth>
                <InputLabel>Campaign Type</InputLabel>
                <Select
                  value={campaignForm.type}
                  label="Campaign Type"
                  onChange={(e) => setCampaignForm({ ...campaignForm, type: e.target.value })}
                >
                  <MenuItem value="Newsletter">Newsletter</MenuItem>
                  <MenuItem value="Promotional">Promotional</MenuItem>
                  <MenuItem value="Welcome">Welcome</MenuItem>
                  <MenuItem value="Transactional">Transactional</MenuItem>
                  <MenuItem value="Event">Event</MenuItem>
                  <MenuItem value="Survey">Survey</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            
            <Grid item xs={12} sm={6}>
              <DateTimePicker
                label="Schedule Send Time"
                value={campaignForm.scheduling?.sendAt ? dayjs(campaignForm.scheduling.sendAt) : null}
                onChange={(value) => setCampaignForm({
                  ...campaignForm,
                  scheduling: { ...campaignForm.scheduling, sendAt: value?.toISOString() || null }
                })}
                sx={{ width: '100%' }}
              />
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Audience Filters
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.audience?.filters?.isLoyaltyMember || false}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        audience: {
                          ...campaignForm.audience,
                          filters: {
                            ...campaignForm.audience.filters,
                            isLoyaltyMember: e.target.checked
                          }
                        }
                      })}
                    />
                  }
                  label="Loyalty Members Only"
                />
              </FormGroup>
            </Grid>
            
            <Grid item xs={12}>
              <Typography variant="subtitle1" gutterBottom>
                Personalization
              </Typography>
              <FormGroup>
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.personalization?.useFirstName || false}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        personalization: {
                          ...campaignForm.personalization,
                          useFirstName: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Use recipient's first name"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.personalization?.loyaltyStatus || false}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        personalization: {
                          ...campaignForm.personalization,
                          loyaltyStatus: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Include loyalty status"
                />
                <FormControlLabel
                  control={
                    <Checkbox
                      checked={campaignForm.personalization?.upcomingReservations || false}
                      onChange={(e) => setCampaignForm({
                        ...campaignForm,
                        personalization: {
                          ...campaignForm.personalization,
                          upcomingReservations: e.target.checked
                        }
                      })}
                    />
                  }
                  label="Show upcoming reservations"
                />
              </FormGroup>
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                multiline
                rows={6}
                label="Email Content (HTML)"
                value={campaignForm.content?.html || ''}
                onChange={(e) => setCampaignForm({
                  ...campaignForm,
                  content: { ...campaignForm.content, html: e.target.value }
                })}
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCampaignDialog(false)}>Cancel</Button>
          <Button onClick={handleSaveCampaign} variant="contained">
            {selectedCampaign ? 'Update' : 'Create'} Campaign
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Email Dialog */}
      <Dialog open={testDialog} onClose={() => setTestDialog(false)}>
        <DialogTitle>Send Test Email</DialogTitle>
        <DialogContent>
          <TextField
            fullWidth
            label="Test Email Address"
            type="email"
            value={testEmail}
            onChange={(e) => setTestEmail(e.target.value)}
            sx={{ mt: 2 }}
          />
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialog(false)}>Cancel</Button>
          <Button onClick={handleTestCampaign} variant="contained" startIcon={<SendIcon />}>
            Send Test
          </Button>
        </DialogActions>
      </Dialog>
    </Container>
  );
};

export default EmailPage;